import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { jwtDecode } from "jwt-decode";
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, AccountInfo, RedirectRequest } from '@azure/msal-browser';
import { TokenService } from '../http/token.service';
import { Observable, from, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, finalize, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { HttpStatus } from '../http/httpStatus';
import { NotifyPopupComponent } from '../common/notify-popup/notify-popup.component';
import { ButtonComponent } from '@usitsdasdesign/dds-ng/button';
import { ModalService } from '@usitsdasdesign/dds-ng/modal';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private tokenRefreshInProgress = false;
  private tokenRefreshedSource = new BehaviorSubject<string | null>(null);
  private tokenRefreshed$ = this.tokenRefreshedSource.asObservable();
 
  constructor(
    private msalService: MsalService,
    private router: Router,
    private modalService: ModalService,
  ) {}

  modalRef: any;
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authToken = localStorage.getItem('idToken');
    const account = this.msalService.instance.getAllAccounts()[0];
    const redirectUrl = this.router.routerState.snapshot.url;
    var redirectRequest: RedirectRequest = {
      redirectStartPage: redirectUrl,
      scopes: ['openid', 'profile'], // Default scopes
    };
    if (req.url.startsWith('https://graph.microsoft.com')||req.url.includes('s3.amazonaws.com')) {
      return next.handle(req);
    }
    
    
    if (authToken && this.isTokenExpired(authToken)) {
      if (this.tokenRefreshInProgress) {
        return this.tokenRefreshed$.pipe(
          filter(token => token !== null),
          take(1),
          switchMap((newAuthToken: string | null) => {
            const authReq = req.clone({
              setHeaders: {
                'Authorization': `Bearer ${newAuthToken}`
              }
            });
            return next.handle(authReq);
          })
        );
      } else if (this.isTokenReadyToExpire(authToken)) {
          console.log('Token is about to expire, refreshing...');
          this.tokenRefreshInProgress = true;
          return this.acquireNewToken(req, next, account, redirectRequest);
        }
    } else if (authToken) {
      // Token is valid, proceed with the request
      const authReq = req.clone({
        setHeaders: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      return next.handle(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === HttpStatus.UNAUTHORIZED) {
            console.error('Unauthorized request', error);
          }
          else if (error.status === HttpStatus.FORBIDDEN) {
            console.error('Forbidden request', error);
            this.router.navigate(['/error']);
          }
          return throwError(() => error);
        })
      );
    } else if (account) {
      // No token, but account exists, acquire a new token silently
      return this.acquireNewToken(req, next, account, redirectRequest);
    } else{
      this.showMsg(req, next, account);
    }

    // Default return in case no conditions are met
    return throwError(() => new HttpErrorResponse({ status: HttpStatus.UNAUTHORIZED, statusText: 'Unauthorized' }));
  }

  isTokenExpired(token: string): boolean {
    const decodedToken = this.decodeToken(token);
    if (!decodedToken) {
      return true;
    }
    const expirationDate = decodedToken.exp * 1000;
    return Date.now() > expirationDate;
  }

  isTokenReadyToExpire(token: string): boolean {
    const decodedToken = this.decodeToken(token);
    if (!decodedToken) {
      return true;
    }
    const expirationTime = decodedToken.exp * 1000; //  Convert to milliseconds
    const refreshTime = expirationTime - Date.now() - 5 * 60 * 1000; // 5 minutes before expiration
    return refreshTime <= 0;
  }

  decodeToken(token: string): any {
    try {
      return jwtDecode(token);
    } catch (error) {
      console.error('Invalid token', error);
      return null;
    }
  }

  handleTokenRefresh(req: HttpRequest<any>, next: HttpHandler, account: AccountInfo): Observable<HttpEvent<any>> {
    // Token is expired, acquire a new token silently
    this.tokenRefreshInProgress = true;
    const redirectUrl = this.router.routerState.snapshot.url;
    console.log('Redirect URL:', redirectUrl);
    var redirectRequest: RedirectRequest = {
      redirectStartPage: redirectUrl,
      scopes: ['openid', 'profile'], // Default scopes
    };
   
    // const account = this.msalService.instance.getAllAccounts()[0];
    if (!account) {
      console.error('No active account found, redirecting to login');
      this.msalService.loginRedirect(redirectRequest);
      return throwError(() => new Error('No active account found'));
    }

    // Token is expired, try to acquire a new token silently
    return this.acquireNewToken(req, next, account, redirectRequest);
  }

  acquireNewToken(req: HttpRequest<any>, next: HttpHandler, account: AccountInfo, redirectRequest: RedirectRequest): Observable<HttpEvent<any>> {
    return from(this.msalService.acquireTokenSilent({
      scopes: ['openid', 'profile'], // Default scopes
      account: account
    })).pipe(
      switchMap((response: AuthenticationResult) => {
        const newIdToken = response.idToken;
        const newAccessToken = response.accessToken;
     localStorage.setItem("Acccess_Token_User", newAccessToken);
     localStorage.setItem("accessToken",newAccessToken); 
     localStorage.setItem("idToken",newIdToken); 
     localStorage.setItem('isAuthorized', "true");
     localStorage.setItem("fromtoken","true");
    this.tokenRefreshedSource.next(newIdToken); // Notify queued requests
        this.tokenRefreshInProgress = false;
        const authReq = req.clone({
          setHeaders: {
            'Authorization': `Bearer ${newIdToken}`
          }
        });

        return next.handle(authReq);
      }),
      catchError((error: any) => {
        console.error('Silent token acquisition failed, redirecting to login', error);
        this.tokenRefreshedSource.next(null); // Notify queued requests
        this.tokenRefreshInProgress = false;
        this.msalService.loginRedirect(redirectRequest);
        return throwError(() => error);
      }),
      finalize(() => {
        this.tokenRefreshInProgress = false;
      }));
  }

  showMsg(req: HttpRequest<any>, next: HttpHandler, account: AccountInfo, showPopup: boolean = false): Observable<HttpEvent<any>> {
    if(showPopup){
      if (this.modalRef) {
        // If a modal is already open, return an empty observable to prevent multiple popups
        return new Observable(observer => observer.complete());
      }
      
      this.modalRef = this.modalService.open(NotifyPopupComponent, { 
        isFooter: true, 
        size: 'sm', 
        isInverse: false, 
        isDeletePopup: false, 
        notifyReason: 'sessionExpired', 
        footerButtonText: 'Refresh',
      });
    }

    return new Observable(observer => {
      this.modalRef.onClosed().subscribe(() => {
        this.modalRef = null; // Reset modalRef when the modal is closed
        this.handleTokenRefresh(req, next, account).subscribe({
          next: (event) => observer.next(event),
          error: (err) => observer.error(err),
          complete: () => observer.complete()
        });
      });
    });
  }
}